import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Save, 
  Trash2, 
  Loader2,
  Eye,
  FileText,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import blogService from '@/services/blogService';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Türkçe karakterli slug oluşturma fonksiyonu
 */
function generateSlug(text) {
  if (!text) return '';
  
  const charMap = {
    'ç': 'c', 'Ç': 'c',
    'ğ': 'g', 'Ğ': 'g',
    'ı': 'i', 'İ': 'i',
    'ö': 'o', 'Ö': 'o',
    'ş': 's', 'Ş': 's',
    'ü': 'u', 'Ü': 'u',
  };
  
  let slug = text.toLowerCase();
  
  // Türkçe karakterleri dönüştür
  Object.keys(charMap).forEach(char => {
    slug = slug.replace(new RegExp(char, 'g'), charMap[char]);
  });
  
  // Özel karakterleri temizle ve boşlukları tire yap
  slug = slug
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');
  
  return slug;
}

/**
 * StaffBlogFormPage - Blog yazısı oluşturma/düzenleme formu
 */
const StaffBlogFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  
  const isEditMode = !!id;
  
  // Form state
  const [formData, setFormData] = useState({
    // Türkçe
    title_tr: '',
    content_tr: '',
    excerpt_tr: '',
    
    // İngilizce
    title_en: '',
    content_en: '',
    excerpt_en: '',
    
    // Arapça
    title_ar: '',
    content_ar: '',
    excerpt_ar: '',
    
    // Rusça
    title_ru: '',
    content_ru: '',
    excerpt_ru: '',
    
    // Genel
    slug: '',
    category: '',
    tags: '',
    featured_image: '',
    status: 'draft',
    
    // SEO
    meta_title: '',
    meta_description: '',
  });
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('tr');

  useEffect(() => {
    if (isEditMode) {
      fetchBlog();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Türkçe başlık değiştiğinde slug otomatik oluştur
  useEffect(() => {
    if (!isEditMode && formData.title_tr) {
      const newSlug = generateSlug(formData.title_tr);
      setFormData(prev => ({ ...prev, slug: newSlug }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.title_tr]);

  const fetchBlog = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data, error } = await blogService.getBlogPostById(id);
      
      if (error) throw error;
      if (!data) throw new Error('Blog yazısı bulunamadı');
      
      // Form verilerini doldur
      setFormData({
        title_tr: data.title_tr || '',
        content_tr: data.content_tr || '',
        excerpt_tr: data.excerpt_tr || '',
        title_en: data.title_en || '',
        content_en: data.content_en || '',
        excerpt_en: data.excerpt_en || '',
        title_ar: data.title_ar || '',
        content_ar: data.content_ar || '',
        excerpt_ar: data.excerpt_ar || '',
        title_ru: data.title_ru || '',
        content_ru: data.content_ru || '',
        excerpt_ru: data.excerpt_ru || '',
        slug: data.slug || '',
        category: data.category || '',
        tags: Array.isArray(data.tags) ? data.tags.join(', ') : '',
        featured_image: data.featured_image || '',
        status: data.status || 'draft',
        meta_title: data.meta_title || '',
        meta_description: data.meta_description || '',
      });
    } catch (err) {
      console.error('Error fetching blog:', err);
      setError(err.message || 'Blog yazısı yüklenirken bir hata oluştu');
      toast({
        variant: 'destructive',
        title: 'Hata',
        description: err.message || 'Blog yazısı yüklenirken bir hata oluştu',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const validateForm = () => {
    if (!formData.title_tr?.trim()) {
      toast({
        variant: 'destructive',
        title: 'Doğrulama Hatası',
        description: 'Türkçe başlık zorunludur.',
      });
      return false;
    }
    
    if (!formData.content_tr?.trim()) {
      toast({
        variant: 'destructive',
        title: 'Doğrulama Hatası',
        description: 'Türkçe içerik zorunludur.',
      });
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e, publishNow = false) => {
    if (e) e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setSaving(true);
      setError(null);
      
      // Tags'i array'e çevir
      const tagsArray = formData.tags
        ? formData.tags.split(',').map(tag => tag.trim()).filter(Boolean)
        : [];
      
      // Veriyi hazırla
      const blogData = {
        title: formData.title_tr, // Ana başlık olarak TR kullan
        title_tr: formData.title_tr,
        title_en: formData.title_en || null,
        title_ar: formData.title_ar || null,
        title_ru: formData.title_ru || null,
        content: formData.content_tr, // Ana içerik olarak TR kullan
        content_tr: formData.content_tr,
        content_en: formData.content_en || null,
        content_ar: formData.content_ar || null,
        content_ru: formData.content_ru || null,
        excerpt: formData.excerpt_tr || null,
        excerpt_tr: formData.excerpt_tr || null,
        excerpt_en: formData.excerpt_en || null,
        excerpt_ar: formData.excerpt_ar || null,
        excerpt_ru: formData.excerpt_ru || null,
        slug: formData.slug || null,
        category: formData.category || null,
        tags: tagsArray.length > 0 ? tagsArray : null,
        featured_image: formData.featured_image || null,
        status: publishNow ? 'published' : formData.status,
        meta_title: formData.meta_title || null,
        meta_description: formData.meta_description || null,
        author_id: user?.id,
      };
      
      if (publishNow) {
        blogData.published_at = new Date().toISOString();
      }
      
      let result;
      if (isEditMode) {
        result = await blogService.updateBlogPost(id, blogData);
        if (result.error) throw result.error;
        toast({
          title: 'Başarılı',
          description: publishNow ? 'Blog yazısı yayınlandı.' : 'Blog yazısı güncellendi.',
        });
      } else {
        result = await blogService.createBlogPost(blogData);
        if (result.error) throw result.error;
        toast({
          title: 'Başarılı',
          description: publishNow ? 'Blog yazısı oluşturuldu ve yayınlandı.' : 'Blog yazısı oluşturuldu.',
        });
        // Yeni blog oluşturulduktan sonra düzenleme moduna geç
        if (result.data?.id) {
          navigate(`/staff/blog/${result.data.id}`, { replace: true });
          return;
        }
      }
      
      navigate('/staff/blog');
    } catch (err) {
      console.error('Error saving blog:', err);
      setError(err.message || 'Blog yazısı kaydedilirken bir hata oluştu');
      toast({
        variant: 'destructive',
        title: 'Hata',
        description: err.message || 'Blog yazısı kaydedilirken bir hata oluştu',
      });
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = () => {
    handleSubmit(null, true);
  };

  const handleSaveDraft = () => {
    const currentStatus = formData.status;
    setFormData(prev => ({ ...prev, status: 'draft' }));
    handleSubmit(null, false).then(() => {
      setFormData(prev => ({ ...prev, status: currentStatus }));
    });
  };

  const handleDelete = async () => {
    if (!id) return;
    
    try {
      setSaving(true);
      const { error } = await blogService.deleteBlogPost(id);
      if (error) throw error;
      
      toast({
        title: 'Başarılı',
        description: 'Blog yazısı başarıyla silindi.',
      });
      navigate('/staff/blog');
    } catch (err) {
      console.error('Error deleting blog:', err);
      toast({
        variant: 'destructive',
        title: 'Hata',
        description: 'Blog yazısı silinirken bir hata oluştu.',
      });
    } finally {
      setSaving(false);
      setDeleteDialogOpen(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin text-muted-foreground" size={48} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Üst Bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/staff/blog">
            <Button variant="outline" size="icon">
              <ArrowLeft size={18} />
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              {isEditMode ? 'Blog Yazısı Düzenle' : 'Yeni Blog Yazısı'}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {isEditMode ? 'Mevcut blog yazısını düzenleyin' : 'Yeni blog yazısı bilgilerini girin'}
            </p>
          </div>
        </div>
        
        {isEditMode && (
          <Button
            variant="destructive"
            onClick={() => setDeleteDialogOpen(true)}
          >
            <Trash2 size={18} className="mr-2" />
            Yazıyı Sil
          </Button>
        )}
      </div>

      {/* Hata Mesajı */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Sol Sütun - Çok Dilli İçerik (2/3) */}
          <div className="lg:col-span-2 space-y-6">
            {/* Çok Dilli İçerik */}
            <Card>
              <CardHeader>
                <CardTitle>Blog İçeriği</CardTitle>
                <CardDescription>Blog yazısını farklı dillerde oluşturun</CardDescription>
              </CardHeader>
              <CardContent>
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <TabsList className="grid w-full grid-cols-4">
                    <TabsTrigger value="tr">Türkçe</TabsTrigger>
                    <TabsTrigger value="en">English</TabsTrigger>
                    <TabsTrigger value="ar">العربية</TabsTrigger>
                    <TabsTrigger value="ru">Русский</TabsTrigger>
                  </TabsList>
                  
                  {/* Türkçe */}
                  <TabsContent value="tr" className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label htmlFor="title_tr">
                        Başlık <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="title_tr"
                        value={formData.title_tr}
                        onChange={(e) => handleChange('title_tr', e.target.value)}
                        placeholder="Blog yazısının başlığı..."
                        required
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="excerpt_tr">Özet</Label>
                      <Textarea
                        id="excerpt_tr"
                        value={formData.excerpt_tr}
                        onChange={(e) => handleChange('excerpt_tr', e.target.value)}
                        placeholder="Kısa bir özet yazın..."
                        rows={3}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="content_tr">
                        İçerik <span className="text-red-500">*</span>
                      </Label>
                      <Textarea
                        id="content_tr"
                        value={formData.content_tr}
                        onChange={(e) => handleChange('content_tr', e.target.value)}
                        placeholder="Blog yazısının detaylı içeriğini yazın..."
                        rows={15}
                        required
                      />
                    </div>
                  </TabsContent>
                  
                  {/* İngilizce */}
                  <TabsContent value="en" className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label htmlFor="title_en">Title</Label>
                      <Input
                        id="title_en"
                        value={formData.title_en}
                        onChange={(e) => handleChange('title_en', e.target.value)}
                        placeholder="Blog post title..."
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="excerpt_en">Excerpt</Label>
                      <Textarea
                        id="excerpt_en"
                        value={formData.excerpt_en}
                        onChange={(e) => handleChange('excerpt_en', e.target.value)}
                        placeholder="Write a short excerpt..."
                        rows={3}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="content_en">Content</Label>
                      <Textarea
                        id="content_en"
                        value={formData.content_en}
                        onChange={(e) => handleChange('content_en', e.target.value)}
                        placeholder="Write the detailed content..."
                        rows={15}
                      />
                    </div>
                  </TabsContent>
                  
                  {/* Arapça */}
                  <TabsContent value="ar" className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label htmlFor="title_ar">عنوان</Label>
                      <Input
                        id="title_ar"
                        value={formData.title_ar}
                        onChange={(e) => handleChange('title_ar', e.target.value)}
                        placeholder="عنوان المقالة..."
                        dir="rtl"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="excerpt_ar">ملخص</Label>
                      <Textarea
                        id="excerpt_ar"
                        value={formData.excerpt_ar}
                        onChange={(e) => handleChange('excerpt_ar', e.target.value)}
                        placeholder="اكتب ملخصًا قصيرًا..."
                        rows={3}
                        dir="rtl"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="content_ar">محتوى</Label>
                      <Textarea
                        id="content_ar"
                        value={formData.content_ar}
                        onChange={(e) => handleChange('content_ar', e.target.value)}
                        placeholder="اكتب المحتوى التفصيلي..."
                        rows={15}
                        dir="rtl"
                      />
                    </div>
                  </TabsContent>
                  
                  {/* Rusça */}
                  <TabsContent value="ru" className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label htmlFor="title_ru">Заголовок</Label>
                      <Input
                        id="title_ru"
                        value={formData.title_ru}
                        onChange={(e) => handleChange('title_ru', e.target.value)}
                        placeholder="Заголовок статьи..."
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="excerpt_ru">Резюме</Label>
                      <Textarea
                        id="excerpt_ru"
                        value={formData.excerpt_ru}
                        onChange={(e) => handleChange('excerpt_ru', e.target.value)}
                        placeholder="Напишите краткое резюме..."
                        rows={3}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="content_ru">Содержание</Label>
                      <Textarea
                        id="content_ru"
                        value={formData.content_ru}
                        onChange={(e) => handleChange('content_ru', e.target.value)}
                        placeholder="Напишите подробное содержание..."
                        rows={15}
                      />
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </div>

          {/* Sağ Sütun - Ayarlar (1/3) */}
          <div className="space-y-6">
            {/* Yayın Ayarları */}
            <Card>
              <CardHeader>
                <CardTitle>Yayın Ayarları</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="status">Durum</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) => handleChange('status', value)}
                  >
                    <SelectTrigger id="status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Taslak</SelectItem>
                      <SelectItem value="review">İncelemede</SelectItem>
                      <SelectItem value="published">Yayında</SelectItem>
                      <SelectItem value="archived">Arşivlenmiş</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="category">Kategori</Label>
                  <Input
                    id="category"
                    value={formData.category}
                    onChange={(e) => handleChange('category', e.target.value)}
                    placeholder="Örn: Gayrimenkul, Yatırım, KKTC..."
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="tags">Etiketler</Label>
                  <Input
                    id="tags"
                    value={formData.tags}
                    onChange={(e) => handleChange('tags', e.target.value)}
                    placeholder="virgülle ayırın: villa, yatırım..."
                  />
                  <p className="text-xs text-muted-foreground">
                    Etiketleri virgülle ayırarak girin
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Slug ve Görsel */}
            <Card>
              <CardHeader>
                <CardTitle>Slug ve Görsel</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="slug">Slug (URL)</Label>
                  <Input
                    id="slug"
                    value={formData.slug}
                    onChange={(e) => handleChange('slug', e.target.value)}
                    placeholder="otomatik-oluşur"
                  />
                  <p className="text-xs text-muted-foreground">
                    Boş bırakırsanız otomatik oluşturulur
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="featured_image">Öne Çıkan Görsel URL</Label>
                  <Input
                    id="featured_image"
                    value={formData.featured_image}
                    onChange={(e) => handleChange('featured_image', e.target.value)}
                    placeholder="https://..."
                  />
                </div>
                
                {formData.featured_image && (
                  <div className="mt-3">
                    <p className="text-sm font-medium mb-2">Önizleme:</p>
                    <img
                      src={formData.featured_image}
                      alt="Önizleme"
                      className="w-full h-32 object-cover rounded border"
                    />
                  </div>
                )}
              </CardContent>
            </Card>

            {/* SEO */}
            <Card>
              <CardHeader>
                <CardTitle>SEO Ayarları</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="meta_title">Meta Başlık</Label>
                  <Input
                    id="meta_title"
                    value={formData.meta_title}
                    onChange={(e) => handleChange('meta_title', e.target.value)}
                    placeholder="SEO başlığı..."
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="meta_description">Meta Açıklama</Label>
                  <Textarea
                    id="meta_description"
                    value={formData.meta_description}
                    onChange={(e) => handleChange('meta_description', e.target.value)}
                    placeholder="SEO açıklaması..."
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Form Altı Butonlar */}
        <div className="flex flex-wrap items-center justify-end gap-4 mt-6 pt-6 border-t">
          <Link to="/staff/blog">
            <Button type="button" variant="outline">
              İptal
            </Button>
          </Link>
          
          <Button
            type="button"
            variant="outline"
            onClick={handleSaveDraft}
            disabled={saving}
          >
            {saving ? (
              <Loader2 className="animate-spin mr-2" size={18} />
            ) : (
              <FileText size={18} className="mr-2" />
            )}
            Taslak Kaydet
          </Button>
          
          <Button
            type="button"
            onClick={handlePublish}
            disabled={saving}
            className="bg-green-600 hover:bg-green-700"
          >
            {saving ? (
              <Loader2 className="animate-spin mr-2" size={18} />
            ) : (
              <Eye size={18} className="mr-2" />
            )}
            Yayınla
          </Button>
          
          <Button type="submit" disabled={saving}>
            {saving ? (
              <>
                <Loader2 className="animate-spin mr-2" size={18} />
                Kaydediliyor...
              </>
            ) : (
              <>
                <Save size={18} className="mr-2" />
                {isEditMode ? 'Güncelle' : 'Kaydet'}
              </>
            )}
          </Button>
        </div>
      </form>

      {/* Silme Onay Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Blog Yazısını Sil</AlertDialogTitle>
            <AlertDialogDescription>
              Bu işlem geri alınamaz. Blog yazısı kalıcı olarak silinecektir.
              <br />
              <br />
              <strong>{formData.title_tr || 'Bu blog yazısı'}</strong> başlıklı
              yazıyı silmek istediğinizden emin misiniz?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>İptal</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default StaffBlogFormPage;
